import React from 'react';
import { Flex, Gutter, Title, BodyText } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import {
  AdminBundleItem,
  VariantOverview,
  AdminCategory,
  LocaleResponse,
  SalesChannel,
  Country,
  BundlePrice,
} from '@cdaas/api-clients/dist/product-service-openapi';

import MainProduct from './MainProduct';
import Addons from './Addons';
import { Errors, VariantEverything } from '../utils';
import { getAutomaticPricingErrors } from './utils';

interface ProductVariantsProps {
  mainItemOptionIds: number[] | undefined;
  handleMainItemOptionIdsChange: (optionIds: number[]) => void;
  items: AdminBundleItem[] | undefined;
  handleItemsChange: (items: AdminBundleItem[]) => void;
  variants: VariantEverything[] | undefined;
  handleVariantsChange: (variants: VariantOverview[]) => Promise<boolean>;
  allCategoriesOfSystem: AdminCategory[];
  allSalesChannelsInSystem: SalesChannel[];
  allCountriesInSystem: Country[];
  bundlePrices: BundlePrice[];
  currentLocale: LocaleResponse;
  locales: LocaleResponse[];
  mainItemError: Errors['mainProductVariants'];
  addonsError: Errors['addonProductVariants'];
}

function ProductVariants(props: ProductVariantsProps) {
  const {
    mainItemOptionIds,
    handleMainItemOptionIdsChange,
    items,
    handleItemsChange,
    variants,
    handleVariantsChange,
    currentLocale,
    locales,
    allCategoriesOfSystem,
    allSalesChannelsInSystem,
    allCountriesInSystem,
    bundlePrices,
    mainItemError,
    addonsError,
  } = props;
  const { t } = useTranslation('bundlescreen');

  const { variantsWithAutomaticPricingErrors, mainItemErroneousSalesChannels, addonItemsErroneousSalesChannels } = getAutomaticPricingErrors(
    bundlePrices,
    mainItemOptionIds,
    items,
    variants || [],
    allSalesChannelsInSystem,
    allCountriesInSystem,
  );

  return (
    <Flex directionColumn>
      <Title size="md" padding={{ right: 'lg' }}>
        {t('product_variants_section.main_product_title')}
      </Title>
      <Gutter />
      <BodyText color="gray400">{t('product_variants_section.main_product_info')}</BodyText>
      <Gutter />
      <MainProduct
        mainItemOptionIds={mainItemOptionIds}
        handleMainItemOptionIdsChange={handleMainItemOptionIdsChange}
        variants={variantsWithAutomaticPricingErrors}
        handleVariantsChange={handleVariantsChange}
        allCategoriesOfSystem={allCategoriesOfSystem}
        erroneousSalesChannels={mainItemErroneousSalesChannels}
        error={mainItemError}
      />
      <Gutter size="xlg" />
      <Title size="md" padding={{ right: 'lg' }}>
        {t('product_variants_section.addons_title')}
      </Title>
      <Gutter />
      <BodyText color="gray400">{t('product_variants_section.addons_info')}</BodyText>
      <Gutter />
      <Addons
        items={items}
        handleItemsChange={handleItemsChange}
        variants={variantsWithAutomaticPricingErrors}
        handleVariantsChange={handleVariantsChange}
        allCategoriesOfSystem={allCategoriesOfSystem}
        currentLocale={currentLocale}
        locales={locales}
        erroneousSalesChannels={addonItemsErroneousSalesChannels}
        errors={addonsError}
      />
      <Gutter />
    </Flex>
  );
}

export default React.memo(ProductVariants);
